import { useState } from 'react'
import { Pill, RefreshCw, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent } from '@components/ui/card'
import { usePortalPrescriptions, useRequestPrescriptionRefill } from '@hooks/usePortal'
import { useToast } from '@hooks/use-toast'
import { cn } from '@lib/utils'

export function PortalPrescriptionsPage() {
  const { toast } = useToast()
  const { data: prescriptions, isLoading } = usePortalPrescriptions()
  const requestRefill = useRequestPrescriptionRefill()

  const [selectedPrescription, setSelectedPrescription] = useState<{
    id: string
    medication_name: string
  } | null>(null)

  const handleRequestRefill = async (data: {
    pickup_preference: 'pharmacy' | 'mail' | 'clinic'
    reason?: string
  }) => {
    if (!selectedPrescription) return

    try {
      await requestRefill.mutateAsync({
        prescription_id: selectedPrescription.id,
        pickup_preference: data.pickup_preference,
        reason: data.reason,
      })
      toast({
        title: 'Refill Requested',
        description: `Your refill request for ${selectedPrescription.medication_name} has been submitted.`,
      })
      setSelectedPrescription(null)
    } catch (error) {
      toast({
        title: 'Request Failed',
        description: 'Please try again later.',
        variant: 'destructive',
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'filled':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'pending':
        return <Clock className="h-4 w-4 text-amber-600" />
      case 'expired':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      default:
        return <XCircle className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string, isActive: boolean) => {
    if (!isActive) return 'bg-gray-100 text-gray-800'
    switch (status) {
      case 'active':
      case 'filled':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-amber-100 text-amber-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const activePrescriptions = prescriptions?.filter((p) => p.is_active) || []
  const inactivePrescriptions = prescriptions?.filter((p) => !p.is_active) || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Prescriptions</h1>
        <p className="text-muted-foreground mt-1">View your medications and request refills</p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-32 animate-pulse"></Card>
          ))}
        </div>
      ) : !prescriptions?.length ? (
        <Card className="p-8 text-center">
          <Pill className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Prescriptions</h3>
          <p className="text-muted-foreground">You don't have any prescriptions on file.</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {activePrescriptions.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Active Prescriptions</h2>
              <div className="space-y-4">
                {activePrescriptions.map((prescription) => (
                  <Card key={prescription.id}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0">
                            <Pill className="h-6 w-6 text-medical-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{prescription.medication_name}</h3>
                            <p className="text-muted-foreground">
                              {prescription.dosage} — {prescription.frequency}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span>
                                Prescribed: {new Date(prescription.prescribed_date).toLocaleDateString()}
                              </span>
                              <span>• {prescription.refills_remaining} refills remaining</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={cn(
                              'px-3 py-1 rounded text-sm font-medium flex items-center gap-1.5',
                              getStatusColor(prescription.status, prescription.is_active)
                            )}
                          >
                            {getStatusIcon(prescription.status)}
                            {prescription.status.charAt(0).toUpperCase() + prescription.status.slice(1)}
                          </span>
                          {prescription.refills_remaining > 0 && prescription.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setSelectedPrescription({
                                  id: prescription.id,
                                  medication_name: prescription.medication_name,
                                })
                              }
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Solicitar resurtido
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {inactivePrescriptions.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Recetas anteriores</h2>
              <div className="space-y-4 opacity-75">
                {inactivePrescriptions.map((prescription) => (
                  <Card key={prescription.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Pill className="h-6 w-6 text-gray-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{prescription.medication_name}</h3>
                          <p className="text-muted-foreground">
                            {prescription.dosage} — {prescription.frequency}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span>
                              Prescribed: {new Date(prescription.prescribed_date).toLocaleDateString()}
                            </span>
                            <span
                              className={cn(
                                'px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1',
                                getStatusColor(prescription.status, prescription.is_active)
                              )}
                            >
                              {getStatusIcon(prescription.status)}
                              {prescription.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {selectedPrescription && (
        <RefillRequestModal
          medicationName={selectedPrescription.medication_name}
          onClose={() => setSelectedPrescription(null)}
          onSubmit={handleRequestRefill}
          isSubmitting={requestRefill.isPending}
        />
      )}
    </div>
  )
}

interface RefillRequestModalProps {
  medicationName: string
  onClose: () => void
  onSubmit: (data: { pickup_preference: 'pharmacy' | 'mail' | 'clinic'; reason?: string }) => void
  isSubmitting: boolean
}

function RefillRequestModal({
  medicationName,
  onClose,
  onSubmit,
  isSubmitting,
}: RefillRequestModalProps) {
  const [formData, setFormData] = useState<{
    pickup_preference: 'pharmacy' | 'mail' | 'clinic'
    reason: string
  }>({
    pickup_preference: 'pharmacy',
    reason: '',
  })

  const pickupOptions = [
    { value: 'pharmacy', label: 'Recoger en farmacia', icon: Pill },
    { value: 'mail', label: 'Envío a domicilio', icon: RefreshCw },
    { value: 'clinic', label: 'Recoger en clínica', icon: CheckCircle },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Solicitar resurtido</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              ✕
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Solicita el resurtido para <strong>{medicationName}</strong>
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit(formData)
          }}
          className="p-6 space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium">Preferencia de entrega</label>
            <div className="grid grid-cols-3 gap-2">
              {pickupOptions.map((option) => {
                const Icon = option.icon
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, pickup_preference: option.value })}
                    className={cn(
                      'flex flex-col items-center gap-2 p-3 rounded-lg border transition-all',
                      formData.pickup_preference === option.value
                        ? 'border-medical-500 bg-medical-50 text-medical-700'
                        : 'border-border hover:bg-muted'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Notas adicionales (opcional)</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Instrucciones especiales o notas para la farmacia"
              className="w-full px-3 py-2 rounded-md border bg-background min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Solicitar resurtido'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
